/**
 * Client side fetch methods.
 * Used for getting data after rendering.
 * https://nextjs.org/docs/basic-features/data-fetching
 *
 * NOTE: "ctx" must be passed to request options if request needs to be authorized in getServerSideProps()
 */

import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import config from 'config';
import jsCookie from 'js-cookie';
import { GetServerSidePropsContext } from 'next';
import { closeSnackbar } from 'notistack';
import { consoleError } from 'utils/log';

import UserService from '../services/user.service';
import store from '../store';
import { logOut } from './authentication';
import { getDefaultLanguageForCountry } from './country-language-mapping';
import i18n, { getCountry, getLanguage } from './i18n';
import { mapInternalUrls, rewriteToInternalUrl } from './internalUrl';
import { getCookies } from './server-cookies';
import { createNotificationAction, notification } from './snackbar';

const axiosWithMetrics = axios.create();

axiosWithMetrics.interceptors.request.use((config) => {
  config.headers['request-startTime'] = new Date().getTime();

  return config;
});

axiosWithMetrics.interceptors.response.use((response) => {
  const currentTime = new Date().getTime();
  const startTime = response.config.headers['request-startTime'];

  response.headers['request-time-ms'] = currentTime - startTime;

  return response;
});

type customOpts = {
  silentFail?: boolean;
  ctx?: GetServerSidePropsContext; // server side context
  errorKey?: string;
};

type sendMetrics = (data: { searchEngine: string; searchTimeMs: number; mode?: string }) => void;

const isDevelopment = ['dev', 'local'].includes(config.get('env'));

// Public→internal base URL pairs, normalised once at module load. On the client
// the `server.internal.*` values are absent (serverRuntimeConfig isn't sent to
// the browser), so every pair is dropped and the mapping is empty.
const internalUrlMapping = mapInternalUrls([
  [config.get('api.url'), config.get('server.internal.apiUrl')],
  [config.get('admin.url'), config.get('server.internal.adminUrl')],
  [config.get('wallet.url'), config.get('server.internal.walletUrl')],
  [config.get('messaging.url'), config.get('server.internal.messagingUrl')],
]);

/**
 * For server-side (SSR) requests, rewrite a public service origin to its
 * internal (private network) counterpart so traffic stays inside the cluster
 * instead of hairpinning out through the public ingress.
 *
 * Only the outbound fetch URL is rewritten — URLs that get serialised into the
 * page for the browser (e.g. OAuth login hrefs) are untouched, because the
 * browser must reach the public hostname.
 *
 * No-op when:
 *  - running in the browser (typeof window !== 'undefined'), or
 *  - the matching FRONT_INTERNAL_* env var is unset (serverRuntimeConfig is
 *    only populated on the server, so the mapping is empty on the client). This
 *    keeps behaviour identical to before until the internal URLs are configured.
 */
function toInternalUrl(url: string): string {
  if (typeof window !== 'undefined') {
    return url;
  }

  return rewriteToInternalUrl(url, internalUrlMapping);
}

export async function GET(url: string, opts: AxiosRequestConfig & customOpts = {}, sendMetrics?: sendMetrics): Promise<any> {
  return request(url, 'GET', undefined, opts, sendMetrics);
}

export async function POST(url: string, body = {}, opts: AxiosRequestConfig & customOpts = {}): Promise<any> {
  return request(url, 'POST', body, opts);
}

export async function PUT(url: string, body = {}, opts: AxiosRequestConfig & customOpts = {}): Promise<any> {
  return request(url, 'PUT', body, opts);
}

export async function PATCH(url: string, body = {}, opts: AxiosRequestConfig & customOpts = {}): Promise<any> {
  return request(url, 'PATCH', body, opts);
}

export async function DELETE(url: string, body = {}, opts: AxiosRequestConfig & customOpts = {}): Promise<any> {
  return request(url, 'DELETE', body, opts);
}

function getRequestOptions(options) {
  const headers: any = {};

  // Authorization token
  const clientSideToken = jsCookie.get('token');
  const serverSideToken = options?.ctx?.req && getCookies(options.ctx.req)?.token;

  if (clientSideToken || serverSideToken) {
    headers.Authorization = clientSideToken || serverSideToken;
  }

  // Language header
  // SSR: Read from cookies (store is empty with factory pattern)
  // Client: Use getLanguage() (reads from store)
  const isSSR = typeof window === 'undefined';

  if (isSSR && options?.ctx?.req) {
    const cookies = getCookies(options.ctx.req);
    const locale = cookies?.['locale'] || `${config.get('countryLanguage')}-${config.get('country')}`;
    const [language, country] = locale.split('-');

    headers['x-language'] = language || config.get('countryLanguage');
    headers['x-country'] = country || config.get('country');
  } else {
    headers['x-language'] = getLanguage() ?? config.get('countryLanguage');
    headers['x-country'] = getCountry() ?? config.get('country');
  }

  return {
    withCredentials: config.get('env') === 'local' ? true : false,
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  };
}

async function request(url, method, body = undefined, opts: AxiosRequestConfig & customOpts = {}, sendMetrics?: sendMetrics) {
  // Resolve once so the request and any error logging point at the same
  // (possibly internal) target — otherwise SSR failures would log the public
  // URL while the actual request went to an internal hostname.
  const resolvedUrl = toInternalUrl(url);

  try {
    const response = await (sendMetrics ? axiosWithMetrics : axios).request({
      url: resolvedUrl,
      method,
      data: body,
      ...getRequestOptions(opts),
    });

    // Check session expired
    checkSessionExpired(response.status, opts);

    // Handle error
    if (response.status < 200 || response.status >= 300) {
      return handleError(new Error('Bad response status: ' + response.status), method, resolvedUrl, response, opts);
    }

    if (sendMetrics && response.headers['request-time-ms']) {
      // Report the arm the backend actually served (advec, legacy-postgres, …) so search latency is
      // attributed to the real A/B arm; fall back to postgres when the response doesn't carry one.
      const servedEngine = response.data?.data?.searchEngine;

      sendMetrics({
        searchEngine: typeof servedEngine === 'string' && servedEngine.trim() ? servedEngine : 'postgres',
        searchTimeMs: response.headers['request-time-ms'],
      });
    }

    return response;
  } catch (err) {
    // Request was aborted (e.g. superseded by a newer search). Don't show an
    // error notification — the caller cancelled it on purpose.
    if (axios.isCancel(err)) {
      return { error: err, canceled: true };
    }

    // Check session expired
    checkSessionExpired(err?.response?.status, opts);

    // Handle error
    return handleError(err, method, resolvedUrl, err && err.response, opts);
  }
}

async function handleError(err: any, method: string, url: string, response?: AxiosResponse, opts: customOpts = {}) {
  consoleError('Request failed:', method, url, err?.message, err?.stack, '\nResponse:', { status: response?.status, data: response?.data });

  // If "silentFail" flag not passed, show global error notification
  if (!opts.silentFail && typeof window !== 'undefined') {
    const errorKey = opts.errorKey || 'common.general-error';
    const errorCode = err?.response?.data?.data?.errorCode;
    const errorMessage = err?.response?.data?.data?.message;

    let message = i18n.t(errorKey);
    let action = undefined;

    console.error(`${errorCode}: ${errorMessage}`);

    // In dev, notify about wrong locale.
    if (isDevelopment && errorCode === 'UNSUPPORTED_LOCALE_ERROR') {
      message = errorMessage;
      const isLoggedIn = store.getState()?.userData?.user?.isLoggedIn;

      // Quick fix button (reset language)
      action = !isLoggedIn
        ? null
        : (snackbarId) =>
            createNotificationAction('Reset language', {
              style: {
                color: 'white',
                border: '1px white solid',
              },
              onClick: async () => {
                const currentCountry = getCountry();
                const resetToLanguage = getDefaultLanguageForCountry(currentCountry);

                await new UserService().updateUserLanguage(resetToLanguage);
                closeSnackbar(snackbarId);
              },
            });
    }

    notification.showErrorNotification(
      {
        message,
        error: err,
      },
      {
        preventDuplicate: true,
        action,
      },
    );
  }

  const apiResponse = (err && err.response) || {};

  return {
    error: err,
    data: response?.data?.data || response?.data,
    status: apiResponse.status,
    message: apiResponse.message,
    response,
    method,
    url,
  };
}

function checkSessionExpired(status: number, opts: AxiosRequestConfig & customOpts) {
  if (status === 401) {
    opts.silentFail = true; // Do not show error when session expired
    logOut(true);
  }
}
